import { Component } from '@theme/component';
import { KlaviyoForm } from '@theme/klaviyo-form';

/** Milliseconds of silence before the type-ahead buffer resets. */
const TYPEAHEAD_RESET_MS = 700;

/** The Launch File form on the case-study page. Tag name only; behavior is `KlaviyoForm`. */
class CaseStudyForm extends KlaviyoForm {}

/**
 * @typedef {Object} PlatformSelectRefs
 * @property {HTMLButtonElement} [trigger] - The combobox button; focus never leaves it.
 * @property {HTMLElement} [menu] - The listbox.
 * @property {HTMLElement[]} [options] - Every option, in DOM order.
 * @property {HTMLElement} [label] - Mirrors the chosen value inside the trigger.
 * @property {HTMLInputElement} [field] - Hidden input the form submission reads.
 * @property {HTMLElement} [status] - Live region announcing how many options there are.
 */

/**
 * @extends {Component<PlatformSelectRefs>}
 */
class PlatformSelect extends Component {
  /** @type {string} */
  #typed = '';
  /** @type {number} */
  #typedAt = 0;
  /** Lives only while the menu is open. @type {AbortController | null} */
  #opened = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#opened?.abort();
    this.#opened = null;
  }

  /** @returns {HTMLElement[]} */
  get #options() {
    return this.refs.options ?? [];
  }

  get #isOpen() {
    return this.refs.trigger?.getAttribute('aria-expanded') === 'true';
  }

  get #isTyping() {
    return this.#typed !== '' && Date.now() - this.#typedAt <= TYPEAHEAD_RESET_MS;
  }

  handleTriggerClick() {
    if (this.#isOpen) {
      this.#close();
      return;
    }

    const options = this.#options;

    this.#open(options[this.#selectedIndex(options)]);
  }

  /**
   * The binding sits on the option itself, so the event target IS the option.
   * @param {Event} event
   */
  handleOptionClick(event) {
    if (event.target instanceof HTMLElement) this.#select(event.target);
  }

  /**
   * Pointer hover keeps the visual highlight and aria-activedescendant in sync.
   * @param {PointerEvent} event
   */
  handleOptionHover(event) {
    if (event.target instanceof HTMLElement) this.#highlight(event.target, false);
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    const options = this.#options;
    if (!options.length) return;

    const current = this.#activeIndex(options);
    const open = this.#isOpen;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (open) this.#highlight(options[Math.min(current + 1, options.length - 1)]);
        else this.#open(options[Math.max(current, 0)]);
        return;

      case 'ArrowUp':
        event.preventDefault();
        if (open) this.#highlight(options[Math.max(current - 1, 0)]);
        else this.#open(current < 0 ? options[options.length - 1] : options[current]);
        return;

      case 'Home':
        if (!open) return;
        event.preventDefault();
        this.#highlight(options[0]);
        return;

      case 'End':
        if (!open) return;
        event.preventDefault();
        this.#highlight(options[options.length - 1]);
        return;

      case ' ':
        event.preventDefault();
        if (this.#isTyping) {
          this.#typeahead(' ');
          return;
        }
        if (open && options[current]) this.#select(options[current]);
        else if (!open) this.#open(options[Math.max(current, 0)]);
        return;

      case 'Enter':
        event.preventDefault();
        if (open && options[current]) this.#select(options[current]);
        else if (!open) this.#open(options[Math.max(current, 0)]);
        return;

      case 'Escape':
        if (!open) return;
        event.preventDefault();
        this.#close();
        return;

      case 'Tab':
        if (open) this.#close(false);
        return;

      default:
        break;
    }

    if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      this.#typeahead(event.key);
    }
  }

  /** @param {PointerEvent} event */
  #handleOutside = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#close(false);
  };

  /**
   * Jumps to the first option starting with the accumulated buffer.
   * @param {string} key
   */
  #typeahead(key) {
    this.#typed = this.#isTyping ? this.#typed + key : key;
    this.#typedAt = Date.now();

    const needle = this.#typed.toLowerCase();
    const match = this.#options.find((option) => (option.dataset.value ?? '').toLowerCase().startsWith(needle));

    if (!match) return;

    if (this.#isOpen) this.#highlight(match);
    else this.#open(match);
  }

  /** @param {HTMLElement} [option] - Option to highlight once open. */
  #open(option) {
    const { trigger, menu } = this.refs;
    if (!trigger || !menu) return;

    const options = this.#options;

    menu.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');

    // Re-opening without an intervening close would otherwise stack listeners.
    this.#opened?.abort();
    this.#opened = new AbortController();
    document.addEventListener('pointerdown', this.#handleOutside, { signal: this.#opened.signal });

    this.#highlight(option ?? options[0]);
    this.#announce(options.length);
  }

  /** @param {boolean} [focusTrigger] */
  #close(focusTrigger = true) {
    const { trigger, menu } = this.refs;
    if (!trigger || !menu) return;

    menu.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    trigger.removeAttribute('aria-activedescendant');

    this.#opened?.abort();
    this.#opened = null;

    this.#announce(null);

    for (const option of this.#options) option.classList.remove('is-active');

    if (focusTrigger) trigger.focus();
  }

  /**
   * @param {HTMLElement} [option]
   * @param {boolean} [scroll] - Scroll the option into view (skip for hover).
   */
  #highlight(option, scroll = true) {
    if (!option) return;

    for (const item of this.#options) item.classList.toggle('is-active', item === option);

    this.refs.trigger?.setAttribute('aria-activedescendant', option.id);

    if (scroll) option.scrollIntoView({ block: 'nearest' });
  }

  /** @param {HTMLElement} option */
  #select(option) {
    const value = option.dataset.value ?? option.textContent?.trim() ?? '';

    for (const item of this.#options) {
      item.setAttribute('aria-selected', String(item === option));
    }

    const { label, field } = this.refs;

    if (label) {
      label.textContent = value;
      delete label.dataset.placeholder;
    }

    if (field) {
      field.value = value;
      field.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.dispatchEvent(new CustomEvent('mtn:platform-change', { bubbles: true, detail: { value } }));

    this.#close();
  }

  /**
   * @param {HTMLElement[]} options
   * @returns {number} Index of the currently chosen option, or -1.
   */
  #selectedIndex(options) {
    return options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
  }

  /**
   * @param {HTMLElement[]} options
   * @returns {number} Index of the visually highlighted option, or -1.
   */
  #activeIndex(options) {
    const id = this.refs.trigger?.getAttribute('aria-activedescendant');
    if (!id) return this.#selectedIndex(options);

    return options.findIndex((option) => option.id === id);
  }

  /**
   * @param {number | null} count - `null` clears the live region.
   */
  #announce(count) {
    const status = this.refs.status;
    if (!status) return;

    status.textContent = count === null ? '' : (status.dataset.statusTemplate ?? '').replace('{count}', String(count));
  }
}

if (!customElements.get('case-study-form')) {
  customElements.define('case-study-form', CaseStudyForm);
}

if (!customElements.get('platform-select')) {
  customElements.define('platform-select', PlatformSelect);
}
